import { motion } from 'framer-motion'
import { 
  Truck, 
  Globe, 
  Clock, 
  Shield, 
  Thermometer, 
  Users, 
  FileText, 
  MapPin,
  CheckCircle,
  Phone,
  Mail
} from 'lucide-react'

const Services = () => {
  const mainServices = [
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Transport Międzynarodowy",
      description: "Regularny przewóz towarów na kluczowych osiach spedycyjnych Europy Zachodniej.",
      features: [
        "Kompleksowy transport drogowy rzeczy",
        "Obsługa ładunków całopojazdowych (FTL) i drobnicowych (LTL)",
        "Sprawny tranzyt przez terytorium Szwajcarii",
        "Pełne wsparcie przy procedurach celnych i dokumentach T1/T2"
      ],
      color: "from-blue-600 to-indigo-600",
      bgLight: "bg-blue-50/50"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Kompleksowa Logistyka",
      description: "Doradztwo spedycyjne i pełne planowanie łańcucha dostaw dla małych i dużych firm.",
      features: [
        "Planowanie optymalnych kosztowo tras przewozowych",
        "Zarządzanie czasem dostaw i logistyka just-in-time",
        "Partnerska współpraca B2B z brokerami spedycyjnymi",
        "Pełne audyty operacji transportowych wewnątrz firmy"
      ],
      color: "from-cyan-500 to-blue-600",
      bgLight: "bg-cyan-50/50"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Transport Ekspresowy",
      description: "Szybkie, bezpośrednie dostawy ładunków o wysokim priorytecie czasowym.",
      features: [
        "Transport dedykowany (bez doładunków po drodze)",
        "Gwarancja ekspresowego czasu dostawy na trasie PL ↔ CH",
        "Monitoring pozycji pojazdu i stały kontakt z kierowcą",
        "Omijanie zatorów dzięki elastycznemu dysponowaniu trasą"
      ],
      color: "from-amber-500 to-orange-600",
      bgLight: "bg-amber-50/50"
    },
    {
      icon: <Thermometer className="w-8 h-8" />,
      title: "Transport Chłodniczy",
      description: "Przewóz ładunków wymagających kontrolowanej temperatury i specjalnych certyfikatów.",
      features: [
        "Nowoczesne chłodnie z rejestratorami temperatur",
        "Transport artykułów spożywczych, rolniczych i chemicznych",
        "Zgodność z unijnymi normami sanitarnymi i HACCP",
        "Ciągły podgląd warunków termicznych w trakcie jazdy"
      ],
      color: "from-teal-500 to-emerald-600",
      bgLight: "bg-teal-50/50"
    }
  ]

  const additionalServices = [
    {
      icon: <Shield className="w-6 h-6 text-primary-600" />,
      title: "Kierowcy Zawodowi",
      description: "Stabilny zespół doświadczonych kierowców z uprawnieniami CE, certyfikatami ADR oraz przeszkoleniem w obsłudze tachografu cyfrowego."
    },
    {
      icon: <FileText className="w-6 h-6 text-primary-600" />,
      title: "Doradztwo Licencyjne",
      description: "Wsparcie prawne przy uzyskiwaniu wypisów z licencji transportowych oraz zgłoszeniach delegowania pracowników (kierowców)."
    },
    {
      icon: <Users className="w-6 h-6 text-primary-600" />,
      title: "Współpraca Partnerska",
      description: "Oferujemy stałą współpracę kontraktową dla zewnętrznych spedytorów, gwarantując niezawodność taboru i stałe stawki frachtowe."
    },
    {
      icon: <MapPin className="w-6 h-6 text-primary-600" />,
      title: "Obsługa Transgraniczna",
      description: "Bezproblemowy przejazd przez granice Szwajcarii (odprawy importowe, eksportowe, rozliczanie opłat drogowych LSVA)."
    }
  ]

  const advantages = [
    "Ponad 7 lat doświadczenia w spedycji i transporcie międzynarodowym (od 2018)",
    "Pełna zgodność prawna w relacjach z BAG (Niemcy) oraz szwajcarskimi organami kantonalnymi",
    "Flota wyposażona w zaawansowane nadajniki GPS i systemy telematyczne",
    "Elastyczność w doborze pojazdów oraz dopasowanie terminów załadunku",
    "Indywidualny opiekun klienta rozmawiający w języku polskim, niemieckim oraz angielskim"
  ]

  const processSteps = [
    {
      num: "1",
      title: "Bezpłatna Konsultacja",
      description: "Omawiamy wymogi ładunku, gabaryty, trasę oraz preferowany czas dostawy."
    },
    {
      num: "2",
      title: "Wycena & Planowanie",
      description: "Przygotowujemy przejrzystą ofertę cenową i wyznaczamy optymalny korytarz tras."
    },
    {
      num: "3",
      title: "Sprawna Realizacja",
      description: "Kierowca ładuje towar, a nasz system dba o bezproblemową odprawę i bezpieczną dostawę."
    }
  ]

  return (
    <div className="min-h-screen bg-slate-50/50 overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-850 to-primary-950 text-white py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(2,132,199,0.1),transparent_50%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-widest bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">Oferta Logistyczna</span>
            <h1 className="text-4xl md:text-6xl font-extrabold font-display tracking-tight leading-tight">
              Nasze Usługi
            </h1>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Świadczymy najwyższej jakości usługi spedycyjne, sprostając wymaganiom najbardziej wymagających przedsiębiorstw.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Co Oferujemy</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
              Główne Kierunki Usług
            </h2>
          </motion.div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl hover:border-slate-200/80 transition-all duration-300 flex flex-col justify-between"
              >
                {/* Header Gradient */}
                <div className={`bg-gradient-to-r ${service.color} p-6 md:p-8 text-white flex justify-between items-center relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                  <div className="space-y-2 relative z-10">
                    <h3 className="text-2xl font-bold font-display leading-tight">{service.title}</h3>
                    <p className="text-primary-100/90 text-sm leading-normal">{service.description}</p>
                  </div>
                  <div className="bg-white/20 p-3.5 rounded-2xl text-white shrink-0 shadow-lg shadow-black/5 relative z-10">
                    {service.icon}
                  </div>
                </div>
                
                {/* Features list */}
                <div className="p-6 md:p-8 space-y-6 flex-1 flex flex-col justify-between bg-slate-50/30">
                  <div className="space-y-3">
                    <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-400">Zakres realizacji:</h4>
                    <ul className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3 text-slate-650 text-sm leading-relaxed">
                          <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-slate-50 relative border-y border-slate-100">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <span className="text-xs font-extrabold text-primary-600 uppercase tracking-widest bg-primary-100/60 py-1.5 px-4 rounded-full">Proste Kroki</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
              Jak Pracujemy?
            </h2>
            <p className="text-slate-500 mt-4 leading-relaxed">
              Zapewniamy przejrzysty i prosty przebieg każdego zlecenia transportowego. Bez zbędnych komplikacji i opóźnień.
            </p>
          </motion.div>

          {/* Timeline Cards Grid */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-md relative text-center flex flex-col items-center card-interactive"
              >
                {/* Number Circle */}
                <div className="w-14 h-14 bg-gradient-to-br from-primary-600 to-secondary-500 rounded-full flex items-center justify-center text-white text-xl font-bold font-display shadow-lg shadow-primary-600/20 mb-6 -mt-14 relative z-10">
                  {step.num}
                </div>
                
                <h3 className="text-xl font-bold font-display text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Usługi Dodatkowe</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
              Komplementarne Wsparcie Transportu
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100/60 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-xl text-primary-600 shrink-0">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-display text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="section-padding bg-slate-900 text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(2,132,199,0.12),transparent_70%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6"
            >
              <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-widest bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">
                Szybki Kontakt
              </span>
              <h2 className="text-3xl md:text-5xl font-extrabold font-display tracking-tight mt-4">
                Chcesz Omówić Szczegóły Frachtu?
              </h2>
              <p className="text-lg text-primary-200/90 leading-relaxed font-light">
                Nasz zespół logistyczny błyskawicznie przeanalizuje parametry Twojego zlecenia. Zadzwoń do nas lub napisz e-mail – chętnie odpowiemy na wszelkie zapytania.
              </p>
              
              {/* Checkmarks */}
              <div className="space-y-3 pt-2">
                {advantages.slice(0, 3).map((adv, idx) => (
                  <div key={idx} className="flex items-center space-x-3 text-sm text-primary-200/90">
                    <CheckCircle className="w-5 h-5 text-secondary-400 shrink-0" />
                    <span>{adv}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Buttons */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="glass-card-dark p-8 md:p-10 border border-white/5 shadow-2xl space-y-6">
                <h3 className="text-xl font-bold font-display text-white mb-2">Wybierz formę kontaktu:</h3>
                
                <a 
                  href="tel:+41762373301" 
                  className="btn-secondary w-full py-4 text-sm font-semibold flex items-center justify-center space-x-3"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  <span>Zadzwoń: +41 76 237 33 01</span>
                </a>
                
                <a 
                  href="mailto:mariusz.sokolowski@fgfalke.eu"
                  className="btn-outline w-full py-4 text-sm font-semibold flex items-center justify-center space-x-3"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  <span>E-mail: mariusz.sokolowski@fgfalke.eu</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Services
